// ── Delad signaturmotor ────────────────────────────────────────────────────────
// Bygger Tikabs mejlsignatur som HTML. Används av Signature-sidan (export till
// Outlook) och av Outlook-mötesbokningen i CRM, så att brödtexten i en inbjudan
// får exakt samma signatur som ett vanligt mejl.
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

export const EMPTY_COMPANY = {
  companyName: '', logoUrl: '', logoWidth: 80,
  website: '', address: '', tagline: '', companyLinkedIn: '',
  nameColor: '#E65100', taglineColor: '#E65100',
};

export const EMPTY_USER = {
  displayName: '', mobile: '', office: '', email: '',
  personalLinkedIn: '', showPersonalLinkedIn: false,
};

export const EMPTY_DEPT = { id: '', name: '', deptTitle: '', roleLabel: '' };

export const SIGNATURE_PLATFORMS = [
  { key: 'desktop', label: 'Outlook Desktop' },
  { key: 'web',     label: 'Outlook Web' },
  { key: 'android', label: 'Android' },
  { key: 'ios',     label: 'iOS' },
];

// ── HTML escaping ──────────────────────────────────────────────────────────────
export function esc(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ── Signature HTML builder ─────────────────────────────────────────────────────
export function buildSignatureHTML(company, dept, user, platform, layout) {
  const isMobile = platform === 'android' || platform === 'ios';
  const isDesktop = platform === 'desktop';
  const font = 'Arial, Helvetica, sans-serif';
  const fs = 9;
  const nameFs = 12;
  const nc = company.nameColor || '#E65100';
  const tc = company.taglineColor || '#E65100';

  // MSO-safe paragraph reset for Outlook Desktop
  const msoReset = isDesktop ? 'mso-margin-top-alt:0;mso-margin-bottom-alt:0;' : '';

  function p(extra, content) {
    return `<p style="margin:0;padding:0;font-family:${font};font-size:${fs}px;color:#333333;${msoReset}${extra}">${content}</p>`;
  }

  const logo = company.logoUrl
    ? `<img src="${esc(company.logoUrl)}" width="${company.logoWidth || 80}" alt="${esc(company.companyName || '')}" border="0" style="display:block;border:0;outline:none;text-decoration:none;">`
    : '';

  // Namnrad + titel + roll (alltid ovanför)
  const headerRows = [];
  headerRows.push(p(`font-size:${nameFs}px;font-weight:bold;color:${nc};`, esc(user.displayName || '')));
  if (dept.deptTitle)
    headerRows.push(p('font-style:italic;color:#555555;', esc(dept.deptTitle)));
  if (dept.roleLabel)
    headerRows.push(p('font-weight:bold;padding-bottom:4px;', esc(dept.roleLabel)));
  const headerHtml = headerRows.join('\n');

  // Kontaktuppgifter – vänster: telefon + e-post, höger: webb + adress + LinkedIn
  const contactL = [];
  const contactR = [];
  if (user.mobile)  contactL.push(p('', esc(user.mobile)));
  if (user.office)  contactL.push(p('', esc(user.office)));
  if (user.email)   contactL.push(p('', `<a href="mailto:${esc(user.email)}" style="color:#333333;text-decoration:none;">${esc(user.email)}</a>`));
  if (company.website)
    contactR.push(p('', `<a href="https://${esc(company.website)}" style="color:#333333;text-decoration:none;">${esc(company.website)}</a>`));
  if (company.address) contactR.push(p('', esc(company.address)));
  const liStyle = `color:#0077B5;text-decoration:none;font-family:${font};font-size:${fs}px;`;
  const hasCompanyLI  = !!company.companyLinkedIn;
  const hasPersonalLI = !!(user.showPersonalLinkedIn && user.personalLinkedIn);
  if (hasCompanyLI || hasPersonalLI) {
    const parts = [];
    if (hasCompanyLI)
      parts.push(`<a href="${esc(company.companyLinkedIn)}" style="${liStyle}">Tikab på LinkedIn ↗</a>`);
    if (hasPersonalLI)
      parts.push(`<a href="${esc(user.personalLinkedIn)}" style="${liStyle}">Min LinkedIn ↗</a>`);
    contactR.push(p('', parts.join('&nbsp;&nbsp;·&nbsp;&nbsp;')));
  }

  // Enkel kolumn (stående/mobil)
  const infoHtml = [...headerRows, ...contactL, ...contactR].join('\n');

  // Horizontal separator
  const sep = `<table cellpadding="0" cellspacing="0" border="0" style="margin:10px 0 6px;">` +
    `<tr><td style="height:1px;background-color:#dddddd;font-size:0;line-height:0;">&nbsp;</td></tr></table>`;

  const tagline = company.tagline
    ? p(`font-size:12px;font-weight:bold;font-style:italic;color:${tc};`, esc(company.tagline))
    : '';

  // ── Landscape: logo | namn/titel/roll + kontakt i två sub-kolumner ────────
  if (layout === 'landscape' && !isMobile) {
    const contactTable = `<table cellpadding="0" cellspacing="0" border="0" style="margin-top:3px;">
<tr valign="top">
  <td style="vertical-align:top;padding-right:14px;white-space:nowrap;">
${contactL.join('\n')}
  </td>
  <td style="vertical-align:top;white-space:nowrap;">
${contactR.join('\n')}
  </td>
</tr>
</table>`;
    return `<table cellpadding="0" cellspacing="0" border="0">
<tr valign="top">
  <td style="vertical-align:top;padding-right:14px;">
${logo}
  </td>
  <td style="vertical-align:top;padding-left:14px;border-left:2px solid #dddddd;">
${headerHtml}
${contactTable}
  </td>
</tr>
<tr>
  <td colspan="2">
${sep}${tagline}
  </td>
</tr>
</table>`;
  }

  // ── Portrait / mobil: en kolumn ───────────────────────────────────────────
  return `<table cellpadding="0" cellspacing="0" border="0">
${logo ? `<tr><td style="padding-bottom:10px;">${logo}</td></tr>` : ''}
<tr><td>
${infoHtml}
</td></tr>
<tr><td>
${sep}${tagline}
</td></tr>
</table>`;
}

// ── Plain text for mobile ──────────────────────────────────────────────────────
export function buildPlainText(company, dept, user) {
  const lines = [];
  if (user.displayName) lines.push(user.displayName);
  if (dept.deptTitle) lines.push(dept.deptTitle);
  if (dept.roleLabel) lines.push(dept.roleLabel);
  lines.push('');
  if (user.mobile) lines.push(user.mobile);
  if (user.office) lines.push(user.office);
  if (user.email) lines.push(user.email);
  if (company.website) lines.push(company.website);
  if (company.address) lines.push(company.address);
  if (company.companyLinkedIn) lines.push(company.companyLinkedIn);
  if (user.showPersonalLinkedIn && user.personalLinkedIn) lines.push(user.personalLinkedIn);
  if (company.tagline) { lines.push(''); lines.push(company.tagline); }
  return lines.join('\n');
}

// ── Ladda signaturunderlag för en användare ────────────────────────────────────
// Samma källor som Signature-sidan: marketingSettings/signatureCompany för
// företagsdelen, users + profiles för personen, companies för logotypen.
export async function loadSignatureContext(currentUser) {
  const [sigSnap, userSnap, profileSnap] = await Promise.all([
    getDoc(doc(db, 'marketingSettings', 'signatureCompany')),
    getDoc(doc(db, 'users', currentUser.uid)),
    getDoc(doc(db, 'profiles', currentUser.uid)),
  ]);

  const u  = userSnap.exists() ? userSnap.data() : {};
  const cv = profileSnap.exists() ? (profileSnap.data().personal || {}) : {};

  const user = {
    displayName:          u.displayName || cv.name || currentUser.displayName || '',
    mobile:               u.signatureMobile           || cv.phone    || '',
    office:               u.signatureOffice           || '',
    email:                (u.email || cv.email || currentUser.email || '').toLowerCase(),
    personalLinkedIn:     u.signaturePersonalLinkedIn || cv.linkedin || '',
    showPersonalLinkedIn: u.signatureShowPersonalLinkedIn ?? false,
  };

  let logoUrl = '';
  if (u.companyId) {
    const orgSnap = await getDoc(doc(db, 'companies', u.companyId));
    if (orgSnap.exists()) logoUrl = orgSnap.data().logo || '';
  }

  // logoUrl kommer alltid från companies-dokumentet – aldrig från sparad marketingSettings
  const { logoUrl: _ignored, ...saved } = sigSnap.exists() ? sigSnap.data() : {};
  const company = { ...EMPTY_COMPANY, ...saved, logoUrl };

  let dept = { ...EMPTY_DEPT };
  if (u.departmentId) {
    const deptSnap = await getDoc(doc(db, 'departments', u.departmentId));
    const dd = deptSnap.exists() ? deptSnap.data() : {};
    dept = {
      id:        u.departmentId,
      name:      dd.name || '',
      deptTitle: dd.signatureDeptTitle || '',
      roleLabel: dd.signatureRoleLabel || '',
    };
  }

  return { company, dept, user, userDoc: u };
}
